import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const Consulting = () => {
  const services = [
    {
      num: "01",
      title: "Audit Technique",
      description: "Analyse complète de votre infrastructure et de votre code pour identifier les axes d'amélioration.",
      features: ["Code review approfondie", "Analyse de performance", "Audit de sécurité"],
    },
    {
      num: "02",
      title: "Conseil en Architecture",
      description: "Conception d'architectures logicielles robustes et évolutives.",
      features: ["Architecture logicielle", "Choix technologiques", "Scalabilité & Cloud"],
    },
    {
      num: "03",
      title: "Formation Équipes",
      description: "Accompagnement et montée en compétences de vos équipes techniques.",
      features: ["Formations sur mesure", "Montée en compétences", "Coaching technique"],
    },
    {
      num: "04",
      title: "Optimisation Process",
      description: "Amélioration de vos processus de développement pour plus d'efficacité.",
      features: ["Méthodologie Agile", "CI/CD & DevOps", "Best practices"],
    },
  ];

  const approach = [
    { num: "01", title: "Diagnostic", desc: "Analyse approfondie de votre situation actuelle" },
    { num: "02", title: "Stratégie", desc: "Définition d'une feuille de route adaptée" },
    { num: "03", title: "Accompagnement", desc: "Mise en œuvre et suivi des recommandations" },
    { num: "04", title: "Transfert", desc: "Autonomisation de vos équipes" },
  ];

  return (
    <div className="bg-gray-950 text-white min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end pb-20">
        <div className="absolute inset-0">
          <img
            src="/images/consulting/1503.jpg"
            alt="Consulting & Stratégie"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/70 to-gray-950/30" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 w-full">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-1 border border-flowkraft-primary/30 text-flowkraft-secondary text-sm mb-6">
              Services
            </div>
            <h1 className="font-display text-display text-white mb-6">
              Consulting & Stratégie
            </h1>
            <p className="text-xl text-gray-300 max-w-xl">
              Accompagnement stratégique pour votre transformation digitale
            </p>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-display text-title text-white mb-6">
                Votre Partenaire Stratégique
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Nous vous accompagnons dans votre transformation digitale avec une
                approche pragmatique et orientée résultats. Notre expertise
                technique et stratégique vous aide à prendre les bonnes décisions
                pour votre business.
              </p>
              <p className="text-gray-400 text-lg leading-relaxed">
                De l'audit initial à la mise en œuvre, nous sommes à vos côtés
                pour garantir le succès de vos projets technologiques.
              </p>
            </div>
            <div className="relative">
              <img
                src="/images/consulting/consulting_expertise.png"
                alt="Strategic Consulting"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 border border-white/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-title text-white mb-16">
            Nos Services de Consulting
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="border border-white/10 p-8 hover:border-flowkraft-primary/50 transition-colors"
              >
                <span className="font-display text-4xl text-flowkraft-primary/30 mb-4 block">
                  {service.num}
                </span>
                <h3 className="font-display text-2xl text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-400 mb-6">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-gray-300 text-sm">
                      <Check className="w-4 h-4 text-flowkraft-secondary mr-3" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-title text-white mb-16">
            Notre Approche
          </h2>

          <div className="grid md:grid-cols-4 gap-px bg-white/10">
            {approach.map((step, index) => (
              <div key={index} className="bg-gray-950 p-6 text-center">
                <div className="font-display text-4xl text-flowkraft-primary/30 mb-4">
                  {step.num}
                </div>
                <h3 className="text-white font-medium mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative">
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/main/cta_background_digital.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <h2 className="font-display text-title text-white mb-6">
            Accélérez votre transformation digitale
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Discutons de vos enjeux stratégiques et technologiques
          </p>
          <Link
            to="/#contact"
            className="btn-solid inline-flex"
          >
            Discutons de vos besoins
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Consulting;
